import { sizeScale } from '@veraclins-dev/utils';

type SizeType = 'size' | 'w' | 'h' | 'min-w' | 'min-h' | 'max-w' | 'max-h';

const extendedSizeScale = [
  ...sizeScale,
  'full',
  'auto',
  'fit',
  'min',
  'max',
  'dvh',
  'dvw',
  'lvw',
  'lvh',
  'svh',
  'svw',
] as const;

const widthScale = [
  ...extendedSizeScale,
  'screen',
  '3xs',
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
] as const;

const heightScale = [...extendedSizeScale, 'screen'] as const;

type ExtendedSizeScale = (typeof extendedSizeScale)[number];

type HeightScale = (typeof heightScale)[number];

// max-width/max-height express "no limit" as `none`, not `auto`; `max-w-auto`
// and `max-h-auto` are not Tailwind utilities and produced no CSS (VWA-169).
type MaxHeightScale = Exclude<HeightScale, 'auto'> | 'none';

type WidthScale = (typeof widthScale)[number];

type MaxWidthScale = Exclude<WidthScale, 'auto'> | 'none';

type SizeValue = Record<ExtendedSizeScale, `${SizeType}-${ExtendedSizeScale}`>;

type WidthValue = Record<WidthScale, `${SizeType}-${WidthScale}`>;

type HeightValue = Record<HeightScale, `${SizeType}-${HeightScale}`>;

type MaxHeightValue = Record<MaxHeightScale, `${SizeType}-${MaxHeightScale}`>;

type MaxWidthValue = Record<MaxWidthScale, `${SizeType}-${MaxWidthScale}`>;

type SizeVariant<T extends SizeType> = T extends 'size'
  ? SizeValue
  : T extends 'w'
    ? WidthValue
    : T extends 'h'
      ? HeightValue
      : T extends 'min-w'
        ? WidthValue
        : T extends 'min-h'
          ? HeightValue
          : T extends 'max-w'
            ? MaxWidthValue
            : T extends 'max-h'
              ? MaxHeightValue
              : never;

const scales = {
  size: extendedSizeScale,
  h: heightScale,
  w: widthScale,
  'min-w': widthScale,
  'min-h': heightScale,
  'max-w': [...widthScale.filter((value) => value !== 'auto'), 'none'],
  'max-h': [...heightScale.filter((value) => value !== 'auto'), 'none'],
} as const;

function generateSizeVariants<T extends SizeType>(sizeType: T): SizeVariant<T> {
  return [...scales[sizeType]].reduce((acc, scale) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is a valid use case
    (acc as any)[scale] = `${sizeType}-${scale}`;
    return acc;
  }, {} as SizeVariant<T>);
}

const sizeVariants = {
  size: generateSizeVariants('size'),
  w: generateSizeVariants('w'),
  h: generateSizeVariants('h'),
  width: generateSizeVariants('w'),
  height: generateSizeVariants('h'),
  minW: generateSizeVariants('min-w'),
  minH: generateSizeVariants('min-h'),
  minWidth: generateSizeVariants('min-w'),
  minHeight: generateSizeVariants('min-h'),
  maxW: generateSizeVariants('max-w'),
  maxH: generateSizeVariants('max-h'),
  maxWidth: generateSizeVariants('max-w'),
  maxHeight: generateSizeVariants('max-h'),
} as const;

type SizeVariants = typeof sizeVariants;

export {
  type ExtendedSizeScale,
  extendedSizeScale,
  type HeightScale,
  heightScale,
  type MaxHeightScale,
  type MaxWidthScale,
  type SizeVariants,
  sizeVariants,
  type WidthScale,
  widthScale,
};
